import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setMobileOpen(false);
    }
  };

  const navLinks = [
    { label: t('nav.home'), id: 'hero' },
    { label: t('nav.services'), id: 'beneficios' },
    { label: t('nav.testimonials'), id: 'depoimentos' },
    { label: t('nav.contact'), id: 'contato' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            onClick={() => scrollTo('hero')}
            className="flex items-center gap-2 cursor-pointer"
            type="button"
          >
            <div className="w-9 h-9 md:w-10 md:h-10 bg-primary-500 rounded-lg flex items-center justify-center">
              <i className="ri-truck-line text-background-50 text-lg md:text-xl" />
            </div>
            <div className="flex flex-col">
              <span className={`font-heading font-bold text-base md:text-lg tracking-tight ${scrolled ? 'text-foreground-950' : 'text-background-50'}`}>
                SPEED
              </span>
              <span className={`font-heading font-semibold text-[10px] md:text-xs tracking-[0.2em] -mt-1 ${scrolled ? 'text-primary-500' : 'text-accent-400'}`}>
                COURIER
              </span>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className={`px-3 py-2 text-sm font-medium transition-colors rounded-md whitespace-nowrap cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-100 hover:text-accent-400'}`}
                type="button"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+552737236099"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-background-100 hover:text-accent-400'}`}
              rel="nofollow"
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <i className={`ri-phone-line ${scrolled ? 'text-primary-500' : 'text-accent-400'}`} />
              </div>
              <span>(27) 3723-6099</span>
            </a>
            <a
              href="https://wa.me/552737236099"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-4 py-2.5 rounded-md text-sm font-semibold transition-colors whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line" />
              </div>
              {t('nav.whatsapp')}
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${scrolled ? 'text-foreground-700' : 'text-background-50'}`}
            type="button"
            aria-label="Menu"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-md border-t border-background-200/70 animate-fade-in">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="w-full text-left px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
                type="button"
              >
                {link.label}
              </button>
            ))}
            <div className="mt-3 pt-3 border-t border-background-200/70 flex flex-col gap-2">
              <a
                href="tel:+552737236099"
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors"
                rel="nofollow"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line text-primary-500" />
                </div>
                (27) 3723-6099
              </a>
              <a
                href="https://wa.me/552737236099"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 px-4 py-3 rounded-md text-sm font-semibold transition-colors"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line" />
                </div>
                {t('nav.whatsapp')}
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}